// covered-by: frontends/carrie/src/routes/ask-puntt/components/__tests__/SpreadsheetCitationDialog.test.tsx
import { X } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Spinner } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { SpreadsheetView } from "../../../components/SpreadsheetView";
import {
  filenameFromSpreadsheetUrl,
  type SpreadsheetCitationAnchor,
} from "../../../components/spreadsheetWorkbook";
import { getSignedPdfUrl } from "../../../services/files";

/**
 * Full-size spreadsheet citation viewer. Reuses SpreadsheetView's gold
 * cellAnchor highlight so the modal outline matches the former sidebar grid.
 */
export function SpreadsheetCitationDialog({
  url,
  cellAnchor,
  fileName,
  onClose,
}: {
  url: string;
  cellAnchor: SpreadsheetCitationAnchor;
  fileName?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation("ui");
  const [publicUrl, setPublicUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setPublicUrl(null);
    getSignedPdfUrl(url)
      .then((signed) => {
        if (!cancelled) setPublicUrl(signed);
      })
      .catch(() => {
        if (!cancelled) {
          // Fall back to the raw URL the same way SpreadsheetViewer does.
          setPublicUrl(url);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  const title =
    fileName?.trim() ||
    filenameFromSpreadsheetUrl(url) ||
    t("spreadsheetCitationContextPanel.header");

  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Content
        className="relative flex max-h-[90vh] w-[min(96vw,1400px)] max-w-[min(96vw,1400px)] flex-col overflow-hidden"
        data-testid="spreadsheet-citation-dialog"
      >
        <Dialog.Close>
          <Button
            variant="ghost"
            size="1"
            color="gray"
            className="absolute right-3 top-3"
            aria-label={t("spreadsheetCitationContextPanel.closeDialog")}
            data-testid="spreadsheet-citation-dialog-close"
          >
            <X size={16} aria-hidden="true" />
          </Button>
        </Dialog.Close>
        <Dialog.Title size="3" className="pr-10">
          {title}
        </Dialog.Title>
        <Dialog.Description className="sr-only">
          {t("spreadsheetCitationContextPanel.dialogDescription")}
        </Dialog.Description>
        <div
          className="mt-3 min-h-0 flex-1 overflow-auto"
          data-testid="spreadsheet-citation-dialog-spreadsheet"
        >
          {publicUrl == null ? (
            <Flex justify="center" className="py-8">
              <Spinner />
            </Flex>
          ) : (
            <div className="h-[min(70vh,720px)] min-h-[320px]">
              <SpreadsheetView url={publicUrl} highlight={cellAnchor} />
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
